import { applySnapshot, flow, types } from "mobx-state-tree";
import api from "services/api";
import { GENDER_TYPE } from "constants/profile";
import { ignoreTimezone } from "utils/date";
import i18next from "i18next";
import { LANGUAGE_STORAGE_KEY } from "i18n/i18n";

const Contacts = types.model({
  email: types.optional(types.string, ""),
  phone: types.optional(types.string, ""),
  intagramHandle: types.optional(types.string, ""),
});

const TimeZone = types.model("TimeZone", {
  id: types.optional(types.string, "+0000"),
  title: types.optional(types.string, "UTC+00"),
});

const Profile = types
  .model({
    accessProgram: types.maybe(types.string),
    name: types.maybe(types.string),
    gender: types.maybe(types.enumeration(Object.values(GENDER_TYPE))),
    dateOfBirth: types.maybeNull(types.string),
    location: types.maybe(types.string),
    image: types.maybe(types.string),
    contacts: types.optional(Contacts, {}),
    localeId: types.maybe(types.string),
    userId: types.maybe(types.number),
    isLoading: types.optional(types.boolean, false),
    isUpdating: types.optional(types.boolean, false),
    isContactsLoading: types.optional(types.boolean, false),
    isPhotoLoading: types.optional(types.boolean, false),
    isPhotoManaging: types.optional(types.boolean, false),
    timezone: types.maybe(TimeZone),
  })
  .views(self => ({
    get email() {
      return self.contacts.email;
    },
  }))
  .actions(self => ({
    update: flow(function* (data: any) {
      try {
        self.isUpdating = true;
        const profile = yield api.put("/mobile/profile", data);
        applySnapshot(self, {
          ...self,
          ...profile,
          dateOfBirth: ignoreTimezone(profile.dateOfBirth),
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isUpdating = false;
      }
    }),

    fetchContacts: flow(function* () {
      try {
        self.isContactsLoading = true;
        const contacts = yield api.get("/mobile/profile/contact");
        applySnapshot(self, { ...self, contacts });
        return contacts;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isContactsLoading = false;
      }
    }),

    updateContacts: flow(function* (data) {
      try {
        self.isContactsLoading = true;
        const contacts = yield api.put("/mobile/profile/contact", data);
        applySnapshot(self, { ...self, contacts });
        return contacts;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isContactsLoading = false;
      }
    }),

    updatePhoto: flow(function* (file) {
      self.isPhotoManaging = true;
      try {
        const formData = new FormData();
        formData.append("file", file);

        const { url } = yield api.uploadFile(
          `/mobile/profile/photo/upload`,
          formData,
        );

        applySnapshot(self, { ...self, image: url });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPhotoManaging = false;
      }
    }),

    deletePhoto: flow(function* () {
      self.isPhotoManaging = true;
      try {
        yield api.delete(`/mobile/profile/photo`);

        applySnapshot(self, { ...self, image: undefined });
      } catch (error) {
        console.log("error :>> ", error);
      } finally {
        self.isPhotoManaging = false;
      }
    }),
  }))
  .actions(self => ({
    fetch: flow(function* () {
      try {
        self.isLoading = true;
        const { data } = yield api.get(
          "/user/api/v1/profile",
          undefined,
          undefined,
          "MSURL",
        );
        applySnapshot(self, {
          ...self,
          ...data,
        });
        if (data.localeId) {
          const language = data?.localeId.toString().substring(0, 2) ?? "en";

          try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
          } catch {
            // localStorage may be unavailable in some contexts
          }

          i18next.changeLanguage(language, () => {
            i18next.reloadResources(language);
          });

          //  StorageService.save("localeId", data.localeId);
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    updateTimeZone: flow(function* (timezone: string) {
      try {
        self.isLoading = true;
        yield api.put(
          "/user/api/v1/profile/timezone",
          {
            timezoneId: timezone,
          },
          undefined,
          "MSURL",
        );
        applySnapshot(self, {
          ...self,
          timezone: { id: timezone },
        });
      } catch (error) {
        console.log("error updateTimeZone: ", error);
      } finally {
        self.isLoading = false;
      }
    }),
  }));

const ProfileStore = Profile.create();

export default ProfileStore;
